import { useEffect, useState, type JSX } from "react";
import { countdownParts, type CountdownParts } from "./countdownParts";
import { useReducedMotion } from "./useReducedMotion";

/** Match the 1000ms tick of the countdown for an even roll rhythm. */
const TICK_MS = 1000;

/** A number cell that rolls up to the next value. */
const RollingValue = ({ value }: { value: string }): JSX.Element => {
  const reducedMotion = useReducedMotion();
  const [previous, setPrevious] = useState(value);
  const [panes, setPanes] = useState(() => ({
    out: null as string | null,
    value,
  }));

  // React's "adjust state on prop change" pattern: schedule the update during
  // render instead of in an effect.
  if (value !== previous) {
    setPrevious(value);
    setPanes(
      reducedMotion ? { out: null, value } : { out: panes.value, value },
    );
  }

  return (
    <span className="line-num">
      {panes.out !== null && (
        <span
          className="cd-pane cd-out"
          aria-hidden="true"
          onAnimationEnd={() =>
            setPanes((current) => ({ ...current, out: null }))
          }
        >
          {panes.out}
        </span>
      )}
      <span className="cd-pane" aria-hidden="true">
        {panes.value}
      </span>
    </span>
  );
};

export interface CountdownLabels {
  days: string;
  hours: string;
  minutes: string;
}

export const Countdown = ({
  deadline,
  labels,
  ariaLabel,
}: {
  deadline: Date;
  labels: CountdownLabels;
  ariaLabel: string;
}): JSX.Element => {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), TICK_MS);
    return () => window.clearInterval(timer);
  }, []);

  const at = (
    parts: CountdownParts,
    label: string,
    kind: keyof CountdownParts,
  ) => (
    <span className="line-unit">
      <span className="line-label">{label}</span>
      <RollingValue value={parts[kind]} />
    </span>
  );

  const parts = countdownParts(deadline, now);

  return (
    <div
      className="countdown countdown-line cd-roll"
      role="group"
      aria-label={ariaLabel}
    >
      {at(parts, labels.days, "days")}
      <span className="line-colon" aria-hidden="true">
        :
      </span>
      {at(parts, labels.hours, "hours")}
      <span className="line-colon" aria-hidden="true">
        :
      </span>
      {at(parts, labels.minutes, "minutes")}
    </div>
  );
};
